import classNames from 'classnames'
import PropTypes from 'prop-types'
import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import normalizeUrl from '../../utils/normalizeUrl'
import Wrapper from './Wrapper'

/**
 * Used in Button.tsx
 */

type Props = {
  ariaLabel?: string
  children: React.ReactNode
  className?: string
  href?: string | null
  onClick?: (event: React.MouseEvent<Element, MouseEvent>) => void
  openNewTab?: boolean
  styled?: boolean
  tabIndex?: number
}

const Link: React.FunctionComponent<Props> = ({
  ariaLabel,
  children,
  className,
  href,
  onClick,
  openNewTab,
  styled,
  tabIndex
}) => {
  if (href == null) {
    return <>{children}</>
  }

  const { isInternal, url } = normalizeUrl(href)
  const isAbsoluteHref = href.includes('//') || href.includes(':')
  const shouldForceExternal = Boolean(openNewTab && isAbsoluteHref)
  const newClassName = classNames(
    'Link',
    !styled && 'Link--Unstyled',
    className
  )

  let link
  if (isInternal && !shouldForceExternal) {
    link = (
      <RouterLink
        aria-label={ariaLabel}
        className={newClassName}
        onClick={onClick}
        to={url}
      >
        {children}
      </RouterLink>
    )
  } else {
    link = (
      /* eslint-disable-next-line react/jsx-no-target-blank */
      <a
        aria-label={ariaLabel}
        className={newClassName}
        href={shouldForceExternal ? href : url}
        onClick={onClick}
        rel={openNewTab ? 'noreferrer noopener' : undefined}
        target={openNewTab ? '_blank' : undefined}
        tabIndex={tabIndex ? tabIndex : 0}
      >
        {children}
      </a>
    )
  }

  return (
    <Wrapper if={isInternal} wrap={(wrappedChildren) => <>{wrappedChildren}</>}>
      {link}
    </Wrapper>
  )
}

Link.propTypes = {
  ariaLabel: PropTypes.string,
  children: PropTypes.node,
  href: PropTypes.string,
  onClick: PropTypes.func,
  openNewTab: PropTypes.bool
}

export default Link
